import React, { useEffect, useRef, useState } from "react"
import * as Chart from "chart.js"
import { DatePicker } from "antd"
import dayjs from "dayjs"
import CategoryDistributionChart from "./CategoryDistributionChart"
import MerTable from "./MerTable"
import TopProductsTable from "./TopProductsTable"
import StoreTable from "./StoreTable"
import { translations } from "../../../utils/Translation/staticTranslation"

const { RangePicker } = DatePicker

Chart.Chart.register(...Chart.registerables)

const MerchantGrowthTrend = ({
  grouthData,
  onMerchantGrowthFilter,
  merchantStoreData,
  onMerchantStoreFilter,
  merchantProductData,
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.MerchantAnalytics || {}
  const [activeTab, setActiveTab] = useState(0)
  const chartRef = useRef(null)
  const chartInstance = useRef(null)
  const productsChartRef = useRef(null)
  const productsChartInstance = useRef(null)

  const [growthFilterDays, setGrowthFilterDays] = useState(30)
  const [countryFlags, setCountryFlags] = useState({})
  const [selectedFilterLabel, setSelectedFilterLabel] = useState(
    lang === "ar" ? "آخر ٣٠ يومًا" : "Last 30 days"
  )
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null,
  })

  // 🏳️‍🌈 Helper to get flag URL by alpha-2 code (ISO)
  const getFlagUrl = (code) => {
    if (!code) return null
    const lower = code.toLowerCase()
    // Using Flagpedia CDN (free, fast, reliable)
    // Example: https://flagcdn.com/w40/pk.png
    return `https://flagcdn.com/w40/${lower}.png`
  }

  // ✅ Date Range logic
  const getDateRange = (label) => {
    const today = dayjs()
    const last7Days = lang === "ar" ? "آخر ٧ أيام" : "Last 7 days"
    const last30Days = lang === "ar" ? "آخر ٣٠ يومًا" : "Last 30 days"
    const last90Days = lang === "ar" ? "آخر ٩٠ يومًا" : "Last 90 days"
    const customRange = lang === "ar" ? "نطاق مخصص" : "Custom Range"

    switch (label) {
      case last7Days:
      case "Last 7 days":
        return {
          startDate: today.subtract(7, "day").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
          filterType: "last7days",
        }
      case last30Days:
      case "Last 30 days":
        return {
          startDate: today.subtract(30, "day").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
          filterType: "last30days",
        }
      case last90Days:
      case "Last 90 days":
        return {
          startDate: today.subtract(90, "day").format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD"),
          filterType: "last90days",
        }
      default:
        return { startDate: null, endDate: null, filterType: "customdate" }
    }
  }

  // ✅ Handle dropdown change
  const handleFilterChange = (value) => {
    setSelectedFilterLabel(value)

    const customRange = lang === "ar" ? "نطاق مخصص" : "Custom Range"

    // If Custom Range is selected, don't call API yet
    if (value === customRange || value === "Custom Range") {
      setCustomDateRange({ startDate: null, endDate: null })
      return
    }

    // For predefined ranges, call API immediately
    const range = getDateRange(value)
    setCustomDateRange(range)

    if (activeTab === 0 && onMerchantGrowthFilter) {
      onMerchantGrowthFilter({
        filterType: range.filterType,
        startDate: range.startDate,
        endDate: range.endDate,
      })
    } else if (activeTab === 1 && onMerchantStoreFilter) {
      onMerchantStoreFilter({
        filterType: range.filterType,
        startDate: range.startDate,
        endDate: range.endDate,
      })
    }
  }

  // ✅ Handle custom date range change
  const handleCustomRangeChange = (dates) => {
    if (dates?.length === 2) {
      const [start, end] = dates
      const range = {
        startDate: start.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
        filterType: "customdate",
      }
      setCustomDateRange(range)

      // Call API with complete date range
      if (activeTab === 0 && onMerchantGrowthFilter) {
        onMerchantGrowthFilter(range)
      } else if (activeTab === 1 && onMerchantStoreFilter) {
        onMerchantStoreFilter(range)
      }
    }
  }

  // ✅ Disable future dates
  const disabledDate = (current) => current && current > dayjs().endOf("day")

  // Prevent duplicate calls on mount
  const hasInitialized = useRef(false)

  // ✅ Initialize default date range on mount
  useEffect(() => {
    // Skip if already initialized - parent component handles initial fetch
    if (hasInitialized.current) {
      return
    }

    hasInitialized.current = true
    const defaultRange = getDateRange(
      lang === "ar" ? "آخر ٣٠ يومًا" : "Last 30 days"
    )
    setCustomDateRange(defaultRange)
    // Don't call onMerchantGrowthFilter here - parent already fetches initial data
    // Only call if parent hasn't provided data yet
    if (onMerchantGrowthFilter && !grouthData) {
      onMerchantGrowthFilter({
        filterType: defaultRange.filterType,
        startDate: defaultRange.startDate,
        endDate: defaultRange.endDate,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 🧩 Fetch and store country flags dynamically
  useEffect(() => {
    const list =
      (activeTab === 1
        ? merchantStoreData?.result?.by_country
        : grouthData?.result?.by_country) || []

    const codes = list.map((c) => c.country).filter(Boolean)
    const newFlags = {}

    codes.forEach((code) => {
      if (!countryFlags[code]) {
        newFlags[code] = getFlagUrl(code)
      } else {
        newFlags[code] = countryFlags[code]
      }
    })

    setCountryFlags((prev) => ({ ...prev, ...newFlags }))
  }, [activeTab, grouthData, merchantStoreData])

  // ✅ Plugin: Small dashed horizontal background lines
  const dashedHorizontalGrid = {
    id: "dashedHorizontalGrid",
    beforeDatasetsDraw(chart) {
      const { ctx, scales } = chart
      const y = scales.y
      const x = scales.x
      if (!y || !x) return
      ctx.save()
      ctx.setLineDash([6, 4])
      ctx.strokeStyle = "#E5E7EB"
      ctx.lineWidth = 1
      y.ticks.forEach((_, i) => {
        const yPos = y.getPixelForTick(i)
        ctx.beginPath()
        ctx.moveTo(x.left, yPos)
        ctx.lineTo(x.right, yPos)
        ctx.stroke()
      })
      ctx.restore()
    },
  }

  // ✅ Products chart - separate effect
  useEffect(() => {
    return () => {
      if (productsChartInstance.current) {
        productsChartInstance.current.destroy()
        productsChartInstance.current = null
      }
    }
  }, [])
  // Replace the Products chart useEffect with this CORRECT version

  useEffect(() => {
    if (activeTab !== 2) return

    const timer = setTimeout(() => {
      if (productsChartInstance.current) {
        productsChartInstance.current.destroy()
        productsChartInstance.current = null
      }

      if (!productsChartRef.current || !merchantProductData?.result?.analytics)
        return

      const ctx = productsChartRef.current.getContext("2d")
      const analytics = merchantProductData.result.analytics

      // ✅ CRITICAL FIX: Maintain the EXACT order from API
      // Object.entries() preserves insertion order in modern JavaScript
      const analyticsArray = Object.entries(analytics).map(([key, data]) => ({
        category: key,
        displayName: key
          .split("-")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" "),
        arabicName: data.category_arabic,
        count: data.count,
        percentage: data.percentage,
      }))

      // console.log("Analytics Array (CHECK THIS):", analyticsArray)

      // ✅ Extract in the SAME order - no sorting, no reordering
      // Use Arabic names if language is Arabic
      const categories =
        lang === "ar"
          ? analyticsArray.map((item) => item.arabicName || item.displayName)
          : analyticsArray.map((item) => item.displayName)
      const values = analyticsArray.map((item) => item.count)
      const percentages = analyticsArray.map((item) => item.percentage)

      // console.log(
      //   "Chair should be:",
      //   analyticsArray.find((a) => a.category === "chair")
      // )

      productsChartInstance.current = new Chart.Chart(ctx, {
        type: "bar",
        data: {
          labels: categories,
          datasets: [
            {
              data: values, // ✅ This MUST match categories order exactly
              backgroundColor: categories.map((_, i) => {
                const pos = i / Math.max(categories.length - 1, 1)
                const r = Math.round(30 + pos * 66)
                const g = Math.round(58 + pos * 107)
                const b = Math.round(138 + pos * 112)
                return `rgb(${r}, ${g}, ${b})`
              }),
              borderWidth: 0,
              barThickness: 24,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: { top: 60, right: 10, bottom: 0, left: 10 },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              backgroundColor: "rgba(17, 24, 39, 0.96)",
              titleColor: "#fff",
              bodyColor: "#fff",
              borderColor: "transparent",
              padding: 10,
              titleFont: { weight: "600", size: 13 },
              bodyFont: { size: 12 },
              displayColors: false,
              caretSize: 6,
              callbacks: {
                title: (items) => {
                  // ✅ Get data using the EXACT index
                  const idx = items[0].dataIndex
                  const data = analyticsArray[idx]

                  if (!data) return items[0].label

                  const lang =
                    typeof window !== "undefined"
                      ? localStorage.getItem("language") || "en"
                      : "en"

                  return lang === "ar"
                    ? data.arabicName || data.displayName
                    : data.displayName
                },
                label: (item) => {
                  // ✅ Get data using the EXACT index
                  const idx = item.dataIndex
                  const data = analyticsArray[idx]

                  if (!data) return `Count: ${item.formattedValue}`

                  return `Count: ${data.count.toLocaleString()} | ${data.percentage.toFixed(
                    2
                  )}%`
                },
              },
            },
            datalabels: {
              display: true,
              anchor: "end",
              align: "end",
              offset: 4,
              rotation: 0,
              color: "#111827",
              font: { size: 9, weight: "500" },
              formatter: (value, ctx) => {
                const label = ctx.chart.data.labels[ctx.dataIndex]
                return label.replace(/-/g, " ")
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                color: "#111827",
                maxRotation: 90,
                minRotation: 90,
                font: { size: 10, weight: "500" },
                autoSkip: false,
                padding: 3,
                callback: function (val, idx) {
                  return this.getLabelForValue(idx).replace(/-/g, " ")
                },
              },
              border: { display: false },
            },
            y: {
              beginAtZero: true,
              max: Math.max(...values) * 1.2,
              ticks: {
                color: "#6B7280",
                font: { size: 11 },
                stepSize: Math.ceil(Math.max(...values) / 5),
                callback: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v),
              },
              grid: { display: false },
              border: { display: false },
            },
          },
        },
        plugins: [dashedHorizontalGrid],
      })
    }, 10)

    return () => clearTimeout(timer)
  }, [activeTab, merchantProductData, lang])

  // ✅ Chart updates when the tab changes
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy()
        chartInstance.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (activeTab === 2) return

    if (chartInstance.current) {
      chartInstance.current.destroy()
      chartInstance.current = null
    }

    if (!chartRef.current || !grouthData?.result?.series) return

    const ctx = chartRef.current.getContext("2d")

    let chartData, chartTitle
    let datasets = []

    // === Merchants Chart ===
    if (activeTab === 0) {
      chartTitle = t.growth?.merchant_growth_trend || "Merchant Growth Trend"
      const series = grouthData.result.series

      // Extract dates and counts from API
      const labels = series.active.map((item) => {
        const date = new Date(item.date)
        return date.getDate() // Get day of month
      })

      const active = series.active.map((d) => d.count)
      const pending = series.pending.map((d) => d.count)
      const waiting = series.waiting.map((d) => d.count)

      const gradientGreen = ctx.createLinearGradient(0, 0, 0, 400)
      gradientGreen.addColorStop(0, "rgba(52, 211, 153, 0.8)")
      gradientGreen.addColorStop(0.4, "rgba(52, 211, 153, 0.5)")
      gradientGreen.addColorStop(0.7, "rgba(52, 211, 153, 0.2)")
      gradientGreen.addColorStop(1, "rgba(52, 211, 153, 0.05)")

      const gradientOrange = ctx.createLinearGradient(0, 0, 0, 600)
      gradientOrange.addColorStop(0, "rgba(251, 146, 60, 0.8)")
      gradientOrange.addColorStop(0.4, "rgba(251, 146, 60, 0.5)")
      gradientOrange.addColorStop(0.7, "rgba(251, 146, 60, 0.2)")
      gradientOrange.addColorStop(1, "rgba(251, 146, 60, 0.05)")

      const gradientBlue = ctx.createLinearGradient(0, 0, 0, 400)
      gradientBlue.addColorStop(0, "rgba(96, 165, 250, 0.8)")
      gradientBlue.addColorStop(0.4, "rgba(96, 165, 250, 0.5)")
      gradientBlue.addColorStop(0.7, "rgba(96, 165, 250, 0.2)")
      gradientBlue.addColorStop(1, "rgba(96, 165, 250, 0.05)")

      chartData = labels
      datasets = [
        {
          label: t.growth?.labels?.active || "Active",
          data: active,
          borderColor: "#34C759",
          backgroundColor: gradientGreen,
          fill: true,
          tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 0,
          pointHoverRadius: 6, // ✅ Size of hover circle
          pointHoverBackgroundColor: "#10B981", // ✅ Fill color same as line
          pointHoverBorderColor: "#fff", // ✅ White border
          pointHoverBorderWidth: 2, // ✅ Border thickness
        },
        {
          label: t.growth?.labels?.pending || "Pending",
          data: pending,
          borderColor: "#FF8D28",
          backgroundColor: gradientOrange,
          fill: true,
          tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: "#F97316",
          pointHoverBorderColor: "#fff",
          pointHoverBorderWidth: 2,
        },
        {
          label: t.growth?.labels?.waiting || "Waiting",
          data: waiting,
          borderColor: "#1099E3",
          backgroundColor: gradientBlue,
          fill: true,
          tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: "#3B82F6",
          pointHoverBorderColor: "#fff",
          pointHoverBorderWidth: 2,
        },
      ]
    }

    // === Stores Chart ===
    if (activeTab === 1) {
      chartTitle = t.growth?.store_growth_trend || "Store Growth Trend"

      // ✅ Use merchantStoreData API instead of grouthData
      const storeSeries = merchantStoreData?.result?.series
      if (!storeSeries?.active || !storeSeries?.total) return

      // ✅ Extract labels (dates) and values (counts)
      const labels = storeSeries.active.map((item) => {
        const date = new Date(item.date)
        return date.getDate() // e.g. 29, 30, 1, 2...
      })

      const active = storeSeries.active.map((d) => d.count)
      const total = storeSeries.total.map((d) => d.count)

      // ✅ Gradients
      const gradientBlue = ctx.createLinearGradient(0, 0, 0, 300)
      gradientBlue.addColorStop(0, "rgba(59,130,246,0.5)")
      gradientBlue.addColorStop(1, "rgba(59,130,246,0.05)")

      const gradientGreen = ctx.createLinearGradient(0, 0, 0, 300)
      gradientGreen.addColorStop(0, "rgba(16,185,129,0.5)")
      gradientGreen.addColorStop(1, "rgba(16,185,129,0.05)")

      // ✅ Chart data from API
      chartData = labels
      datasets = [
        {
          label: t.growth?.labels?.total_stores || "Total Stores",
          data: total,
          borderColor: "#3B82F6",
          backgroundColor: gradientBlue,
          fill: true,
          tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 0,
        },
        {
          label: t.growth?.labels?.active_stores || "Active Stores",
          data: active,
          borderColor: "#10B981",
          backgroundColor: gradientGreen,
          fill: true,
          tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 0,
        },
      ]
    }

    // ✅ Hover vertical dashed line plugin
    const hoverLine = {
      id: "hoverLine",
      afterDraw: (chart) => {
        if (chart.tooltip?._active?.length) {
          const ctx = chart.ctx
          const activePoint = chart.tooltip._active[0]
          const x = activePoint.element.x
          const topY = chart.scales.y.top
          const bottomY = chart.scales.y.bottom

          ctx.save()
          ctx.beginPath()
          ctx.setLineDash([6, 4])
          ctx.moveTo(x, topY)
          ctx.lineTo(x, bottomY)
          ctx.lineWidth = 1
          ctx.strokeStyle = "#9CA3AF"
          ctx.stroke()
          ctx.restore()
        }
      },
    }

    // Find max value for dynamic scaling
    const maxValue = Math.max(...datasets.flatMap((ds) => ds.data))
    const yAxisMax = Math.ceil(maxValue * 1.2) // 20% padding

    chartInstance.current = new Chart.Chart(ctx, {
      type: "line",
      data: { labels: chartData, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: {
              usePointStyle: true,
              pointStyle: "circle",
              boxWidth: 10,
              boxHeight: 10,
              padding: 30, // ✅ Adds spacing between legend items
              color: "#374151",
              generateLabels: (chart) => {
                const { data } = chart
                if (!data.datasets.length) return []
                return data.datasets.map((dataset, i) => ({
                  text: dataset.label,
                  fillStyle: dataset.borderColor, // ✅ Use dataset border color for legend fill
                  strokeStyle: dataset.borderColor,
                  lineWidth: 0, // ✅ Removes border outline
                  hidden: !chart.isDatasetVisible(i),
                  datasetIndex: i,
                }))
              },
            },
          },
          tooltip: {
            backgroundColor: "rgba(255,255,255,0.95)",
            titleColor: "#111827",
            bodyColor: "#111827",
            borderColor: "#E5E7EB",
            borderWidth: 1,
            padding: 10,
            titleFont: { weight: "bold" },
            displayColors: true,
          },
        },
        layout: {
          padding: { left: 30, right: 30, bottom: 30, top: 20 }, // ✅ Adds X-axis padding
        },

        scales: {
          x: {
            grid: { display: false },
            ticks: { color: "#6B7280" },
          },
          y: {
            beginAtZero: true,
            max: yAxisMax > 0 ? yAxisMax : 10,
            ticks: {
              color: "#6B7280",
              callback: (v) => (v >= 1000 ? `${v / 1000}K` : v),
            },
            grid: { display: false },
          },
        },
      },
      plugins: [hoverLine, dashedHorizontalGrid],
    })
  }, [activeTab, grouthData, merchantStoreData])

  // Convert API country data to display format
  // ✅ Convert API country data depending on active tab
  const countries =
    (activeTab === 1
      ? merchantStoreData?.result?.by_country
      : grouthData?.result?.by_country
    )?.map((country, index) => {
      const code = country.country
      return {
        id: index + 1,
        flagUrl: countryFlags[code] || null,
        name: code,
        count: country.count.toLocaleString(),
        percent: `${country.percent.toFixed(0)}%`,
      }
    }) || []

  const tabs = [
    t.growth?.tabs?.merchants || "Merchants",
    t.growth?.tabs?.stores || "Stores",
    t.growth?.tabs?.products || "Products",
  ]

  const renderProductsChart = () => (
    <div
      className="merch-dash-card merch-dash-chart-card"
      style={{ gridColumn: "1 / -1" }}
    >
      <div className="merch-dash-card-header">
        <h3>
          {t.growth?.distribution_by_category || "Distribution by Category"}
        </h3>
      </div>
      <div className="merch-dash-chart-container" style={{ height: "500px" }}>
        <canvas ref={productsChartRef} key="products-chart"></canvas>
      </div>
    </div>
  )

  const renderChartCard = (title) => {
    const customRange = lang === "ar" ? "نطاق مخصص" : "Custom Range"
    const isCustomRange =
      selectedFilterLabel === customRange ||
      selectedFilterLabel === "Custom Range"

    // ✅ Dropdown options
    const filterOptions =
      lang === "ar"
        ? [
            { label: "آخر ٧ أيام", value: "آخر ٧ أيام" },
            { label: "آخر ٣٠ يومًا", value: "آخر ٣٠ يومًا" },
            { label: "آخر ٩٠ يومًا", value: "آخر ٩٠ يومًا" },
            { label: "نطاق مخصص", value: "نطاق مخصص" },
          ]
        : [
            { label: t.growth?.days_7 || "Last 7 days", value: "Last 7 days" },
            {
              label: t.growth?.days_30 || "Last 30 days",
              value: "Last 30 days",
            },
            {
              label: t.growth?.days_90 || "Last 90 days",
              value: "Last 90 days",
            },
            {
              label: t.filter?.custom_range || "Custom Range",
              value: "Custom Range",
            },
          ]

    return (
      <div className="merch-dash-card merch-dash-chart-card">
        <div className="merch-dash-card-header">
          <h3>{title}</h3>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {/* ✅ Range Picker - shown when Custom Range is selected */}
            {isCustomRange && (
              <RangePicker
                allowClear={false}
                disabledDate={disabledDate}
                onChange={handleCustomRangeChange}
                value={
                  customDateRange.startDate && customDateRange.endDate
                    ? [
                        dayjs(customDateRange.startDate),
                        dayjs(customDateRange.endDate),
                      ]
                    : null
                }
                format="YYYY-MM-DD"
                style={{
                  height: "40px",
                  marginRight: lang === "ar" ? 0 : "10px",
                  marginLeft: lang === "ar" ? "10px" : 0,
                }}
              />
            )}
            {/* ✅ Dropdown Select */}
            <div
              style={{
                position: "relative",
                display: "inline-block",
                width: "auto",
              }}
            >
              <select
                className="merch-dash-select"
                style={{
                  paddingTop: "8px",
                  paddingBottom: "8px",
                  paddingRight: lang === "ar" ? "10px" : "40px",
                  paddingLeft: lang === "ar" ? "40px" : "10px",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  fontSize: "14px",
                }}
                value={selectedFilterLabel}
                onChange={(e) => handleFilterChange(e.target.value)}
              >
                {filterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <img
                src="/icons/selectstoreanalytics.svg"
                alt="dropdown-icon"
                style={{
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  right: lang === "ar" ? "auto" : "10px",
                  left: lang === "ar" ? "10px" : "auto",
                  width: "22px",
                  height: "22px",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
            </div>
          </div>
        </div>
        <div className="merch-dash-chart-container">
          <canvas ref={chartRef} key="main-chart"></canvas>
        </div>
      </div>
    )
  }

  const renderCountryCard = (title) => (
    <div className="merch-dash-card merch-dash-country-card">
      <div className="insightsbc-card-country">
        <div>
          <h3 className="merch-dash-country-title">{title}</h3>
        </div>
        <div>
          <span className="country-span">Countries </span>
          <span className="country-count-bold">
            ({grouthData?.result?.total_countries})
          </span>
        </div>
      </div>
      <div className="merch-dash-country-list">
        {countries.map((c) => (
          <div key={c.id} className="merch-dash-country-item">
            <div className="merch-dash-country-left">
              <span className="merch-dash-rank">
                {String(c.id).padStart(2, "0")}
              </span>
              {c.flagUrl ? (
                <img
                  src={c.flagUrl}
                  alt={`${c.name} flag`}
                  style={{ width: "20px", marginRight: "8px" }}
                />
              ) : (
                <span className="merch-dash-flag">🏳️</span>
              )}
              <span className="merch-dash-name">{c.name}</span>
            </div>
            <div className="merch-dash-country-right">
              <div className="merch-dash-count">{c.count}</div>
              <div className="merch-dash-percent">{c.percent}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const renderTabContent = () => {
    if (activeTab === 0)
      return (
        <>
          <div className="merch-dash-grid">
            {renderChartCard(
              t.growth?.merchant_growth_trend || "Merchant Growth Trend"
            )}
            {renderCountryCard(
              t.growth?.merchants_by_country || "Merchants by Country"
            )}
          </div>

          <MerTable grouthData={grouthData} />
        </>
      )

    if (activeTab === 1)
      return (
        <>
          <div className="merch-dash-grid">
            {renderChartCard(
              t.growth?.store_growth_trend || "Store Growth Trend"
            )}
            {renderCountryCard(
              t.growth?.stores_by_country || "Stores by Country"
            )}
          </div>

          <StoreTable merchantStoreData={merchantStoreData} />
        </>
      )

    if (activeTab === 2)
      return (
        <>
          <div className="product-barchart-chart">
            <CategoryDistributionChart
              analyticsData={merchantProductData?.result?.analytics || {}}
            />
          </div>
          <div>
            <TopProductsTable merchantProductData={merchantProductData} />
          </div>
        </>
      )

    // if (activeTab === 3)
    //   return (
    //     <>
    //       <div className="merch-dash-card">
    //         <h3 className="merch-dash-country-title">Furniture Finder</h3>
    //         <p style={{ color: "#6B7280", marginTop: "10px" }}>
    //           Furniture search and discovery tools will be displayed here.
    //         </p>
    //       </div>
    //     </>
    //   )
  }

  return (
    <div className="merch-dash-wrapper">
      <div className="merch-dash-tabs">
        {tabs.map((tab, i) => (
          <button
            key={i}
            className={`merch-dash-tab-btn ${
              i === activeTab ? "merch-dash-tab-active" : ""
            }`}
            onClick={() => setActiveTab(i)}
          >
            {tab}
          </button>
        ))}
      </div>

      {renderTabContent()}
    </div>
  )
}

export default MerchantGrowthTrend
